/**
 * Permission Management Component
 * 
 * This component provides an interface for admins to manage
 * user permissions for accessing graphs and data.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Autocomplete,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthProvider';
import PermissionGate from '../auth/PermissionGate';
import adminApi from '../../services/adminApi';
import { api } from '../../api/setup';
import type { PermissionLevel } from '../../types';
import './admin.css';

interface PermissionManagementProps {
  /** When true, render without PermissionGate (parent must gate). Used inside Users & permissions. */
  embedded?: boolean;
  /** Called after bulk/single permission changes so parent can refresh the user matrix. */
  onEmbeddedDataChanged?: () => void;
}

const PermissionManagement: React.FC<PermissionManagementProps> = ({
  embedded = false,
  onEmbeddedDataChanged,
}) => {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<number>(0);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Permission data
  const [valueChainPermissions, setValueChainPermissions] = useState<any[]>([]);
  const [countryPermissions, setCountryPermissions] = useState<any[]>([]);
  const [dataPermissions, setDataPermissions] = useState<any[]>([]);
  // Countries tab: include inactive toggle
  const [showInactiveCountries, setShowInactiveCountries] = useState<boolean>(false);
  // Graph Sources tab: include inactive toggle
  const [showInactiveGraphSources, setShowInactiveGraphSources] = useState<boolean>(false);
  // Data Access tab: include inactive when Status=All
  const [showInactiveData, setShowInactiveData] = useState<boolean>(false);
  // Financial Data tab: include inactive toggle (applies to selected user list)
  const [showInactiveFinancial, setShowInactiveFinancial] = useState<boolean>(false);
  // User filters for Graph Sources and Countries tabs
  const [selectedGraphUserId, setSelectedGraphUserId] = useState<number | ''>('');
  const [selectedCountryUserId, setSelectedCountryUserId] = useState<number | ''>('');
  // Financial tab pagination
  const [financialPage, setFinancialPage] = useState<number>(1);
  const financialPerPage = 50;
  const [financialTotal, setFinancialTotal] = useState<number>(0);
  // Financial data permissions (per selected user)
  const [financialPermissions, setFinancialPermissions] = useState<any[]>([]);
  const [financialSources, setFinancialSources] = useState<Array<{ country: string; source: string; label: string }>>([]);
  const [selectedFinancialUserId, setSelectedFinancialUserId] = useState<number | ''>('');
  const [permissionType, setPermissionType] = useState<string>('value_chain');
  const [addPermissionDialog, setAddPermissionDialog] = useState<boolean>(false);
  
  // Data for dropdowns
  const [users, setUsers] = useState<any[]>([]);
  const [valueChains, setValueChains] = useState<any[]>([]);
  const [graphSources, setGraphSources] = useState<Array<{ source_db: string; label: string; count: number }>>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  // Data Access filters
  const [dataTypeOptions, setDataTypeOptions] = useState<string[]>([]);
  const [dataFilterUserId, setDataFilterUserId] = useState<number | ''>('');
  const [dataFilterType, setDataFilterType] = useState<string>('');
  const [dataFilterStatus, setDataFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [dataFilterSearch, setDataFilterSearch] = useState<string>('');
  const [dataPage, setDataPage] = useState<number>(1);
  const dataPerPage = 25;

  // TOTP dialog for self-admin deactivation
  const [totpOpen, setTotpOpen] = useState<boolean>(false);
  const [totpCode, setTotpCode] = useState<string>('');
  const [pendingToggle, setPendingToggle] = useState<{ id: number; newStatus: boolean } | null>(null);
  const [pendingBulk, setPendingBulk] = useState<{ ids: number[]; newStatus: boolean } | null>(null);

  // Bulk selection per tab
  const [selectedGraphSourceIds, setSelectedGraphSourceIds] = useState<number[]>([]);
  const [selectedCountryIds, setSelectedCountryIds] = useState<number[]>([]);
  const [selectedDataIds, setSelectedDataIds] = useState<number[]>([]);
  const [selectedFinancialIds, setSelectedFinancialIds] = useState<number[]>([]);
  
  // Form data for add permission
  const [formData, setFormData] = useState({
    userIds: [], // Changed to array for multi-select
    permissionLevel: 'read',
    isActive: true,
    notes: '',
    // Value chain specific
    valueChainIds: [], // Changed to array for multi-select
    valueChainNames: [], // Changed to array for multi-select
    // Country specific
    countryCodes: [], // Changed to array for multi-select
    countryNames: [], // Changed to array for multi-select
    // Data permission specific
    dataType: '',
    dataIdentifier: '',
    dataMetadata: null,
    // Financial specific
    financialCombos: [] as Array<{ country: string; source: string }>
  });

  // Note: mock permissions removed; use live API data

  const notifyEmbeddedParent = useCallback(() => {
    if (embedded && onEmbeddedDataChanged) onEmbeddedDataChanged();
  }, [embedded, onEmbeddedDataChanged]);

  useEffect(() => {
    loadPermissions();
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    setLoadingDropdowns(true);
    try {
      // Load dropdown data
      
      // Load each API call individually to handle failures gracefully
      const loadUsers = async () => {
        try {
          const data = await adminApi.getUsers();
          return data.users || [];
        } catch (err) {
          console.error('PermissionManagement - Error loading users:', err);
          return [];
        }
      };
      
      const loadValueChains = async () => {
        try {
          // Legacy value chain list (may be unused when using graph sources)
          const data = await adminApi.getValueChains();
          return Array.isArray(data) ? data : [];
        } catch (err) {
          console.error('PermissionManagement - Error loading value chains:', err);
          return [];
        }
      };
      const loadCountries = async () => {
        try {
          const data = await adminApi.getCountries();
          return Array.isArray(data) ? data : [];
        } catch (err) {
          console.error('PermissionManagement - Error loading countries:', err);
          return [];
        }
      };
      const loadGraphSources = async () => {
        try {
          let data = await adminApi.getGraphSources(true);
          if (Array.isArray(data) && data.length > 0) return data;
        } catch (err) {
          console.error('PermissionManagement - Error loading graph sources (admin endpoint):', err);
        }
        // Fallback: derive from available roots when admin endpoint returns empty
        try {
          const rootsResp: any = await api.graphData.getAvailableRootNodesApiV1GraphRootsGet();
          const roots = Array.isArray(rootsResp?.data) ? rootsResp.data : (Array.isArray(rootsResp) ? rootsResp : []);
          const srcSet = new Set<string>();
          (roots || []).forEach((n: any) => { if (n && n.source_db) srcSet.add(String(n.source_db)); });
          const derived = Array.from(srcSet).map(src => ({ source_db: src, count: 0, label: src }));
          return derived;
        } catch (err) {
          console.error('PermissionManagement - Error deriving graph sources from roots:', err);
          return [];
        }
      };
      const loadFinancialSources = async () => {
        try {
          const resp = await adminApi.getFinancialSources();
          const arr = Array.isArray(resp?.data) ? resp.data : [];
          return arr;
        } catch (err) {
          console.error('PermissionManagement - Error loading financial sources:', err);
          return [];
        }
      };
      
      // Load all data in parallel, but handle individual failures
      const [usersArray, valueChainsArray, countriesArray, graphSourcesArray, financialSourcesArray] = await Promise.all([
        loadUsers(),
        loadValueChains(),
        loadCountries(),
        loadGraphSources(),
        loadFinancialSources()
      ]);
      
      setUsers(usersArray);
      setValueChains(valueChainsArray);
      setCountries(countriesArray);
      setGraphSources(graphSourcesArray);
      setFinancialSources(financialSourcesArray);
      try {
        const types = await adminApi.getDataTypes();
        // Only expose data access types that are actually enforced server-side
        const EFFECTIVE_TYPES = ['admin', 'prodcom_view'];
        const cleaned = Array.isArray(types) ? types.filter((t) => !!t) : [];
        const filtered = cleaned.filter((t) => EFFECTIVE_TYPES.includes(t));
        setDataTypeOptions(filtered.length ? filtered : EFFECTIVE_TYPES);
      } catch (e) {
        // non-fatal
      }
    } catch (err) {
      console.error('PermissionManagement - Unexpected error loading dropdown data:', err);
      setError('Failed to load dropdown data');
    } finally {
      setLoadingDropdowns(false);
    }
  };

  const loadPermissions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [vcPerms, countryPerms, dataPerms] = await Promise.all([
        adminApi.getValueChainPermissions(),
        adminApi.getCountryPermissions(),
        adminApi.getDataPermissions()
      ]);

      // For Graph Sources tab, reuse data permissions filtered to data_type === 'graph_source'
      const allData = dataPerms.permissions || [];
      setValueChainPermissions(allData.filter((p: any) => p.data_type === 'graph_source'));
      // Store full list; filter in view by 'showInactiveCountries'
      setCountryPermissions(countryPerms.countries || []);
      setDataPermissions(allData);
    } catch (err) {
      setError('Failed to load permissions');
      console.error('Error loading permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFinancialPermissionsForUser = async (userId: number) => {
    if (!userId) { setFinancialPermissions([]); return; }
    try {
      const resp = await adminApi.getFinancialPermissions({ user_id: userId, page: financialPage, limit: financialPerPage });
      const perms = Array.isArray(resp?.permissions) ? resp.permissions : [];
      setFinancialPermissions(perms);
      if (typeof resp?.total === 'number') setFinancialTotal(resp.total);
    } catch (err) {
      console.error('Error loading financial permissions for user:', err);
      setFinancialPermissions([]);
    }
  };
  const loadAllFinancialPermissions = async () => {
    try {
      const resp = await adminApi.getFinancialPermissions({ page: financialPage, limit: financialPerPage });
      const perms = Array.isArray(resp?.permissions) ? resp.permissions : [];
      setFinancialPermissions(perms);
      if (typeof resp?.total === 'number') setFinancialTotal(resp.total);
    } catch (err) {
      console.error('Error loading all financial permissions:', err);
      setFinancialPermissions([]);
    }
  };

  const handleAddPermission = () => {
    setFormData({
      userIds: [],
      permissionLevel: 'read',
      isActive: true,
      notes: '',
      valueChainIds: [],
      valueChainNames: [],
      countryCodes: [],
      countryNames: [],
      dataType: '',
      dataIdentifier: '',
      dataMetadata: null,
      financialCombos: []
    });
    setAddPermissionDialog(true);
  };

  const handleFormSubmit = async () => {
    try {
      const baseData = {
        permission_level: formData.permissionLevel,
        is_active: formData.isActive,
        notes: formData.notes
      };

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Handle multiple users and multiple resources
      for (const userId of formData.userIds) {
        try {
          const userBaseData = { 
            ...baseData, 
            user_id: userId,
            permission_level: formData.permissionLevel as PermissionLevel
          };

          switch (permissionType) {
            case 'value_chain':
              // Interpret as Graph Source Access
              for (let i = 0; i < formData.valueChainIds.length; i++) {
                await adminApi.grantDataPermission({
                  ...userBaseData,
                  data_type: 'graph_source',
                  data_identifier: formData.valueChainIds[i],
                  metadata: null
                });
                successCount++;
              }
              break;
            case 'country':
              for (let i = 0; i < formData.countryCodes.length; i++) {
                await adminApi.grantCountryPermission({
                  ...userBaseData,
                  country_code: formData.countryCodes[i],
                  country_name: formData.countryNames[i]
                });
                successCount++;
              }
              break;
            case 'data':
              await adminApi.grantDataPermission({
                ...userBaseData,
                data_type: formData.dataType,
                data_identifier: formData.dataIdentifier || formData.dataType,
                metadata: formData.dataMetadata ? JSON.stringify(formData.dataMetadata) : null
              });
              successCount++;
              break;
            case 'financial':
              for (const combo of (formData.financialCombos || [])) {
                await adminApi.grantFinancialDataPermission({
                  user_id: userId,
                  country: combo.country,
                  source: combo.source,
                  permission_level: formData.permissionLevel,
                  notes: formData.notes
                });
                successCount++;
              }
              break;
            default:
              throw new Error('Invalid permission type');
          }
        } catch (err) {
          errorCount++;
          errors.push(`User ${userId}: ${err.response?.data?.detail || 'Failed to grant permission'}`);
        }
      }

      await loadPermissions(); // Reload permissions
      if (permissionType === 'financial' && selectedFinancialUserId) {
        await loadFinancialPermissionsForUser(Number(selectedFinancialUserId));
      }
      notifyEmbeddedParent();
      setAddPermissionDialog(false);
      
      if (errorCount === 0) {
        setSuccess(`${successCount} permission(s) granted successfully`);
      } else if (successCount > 0) {
        setSuccess(`${successCount} permission(s) granted successfully, ${errorCount} failed`);
        setError(errors.join('; '));
      } else {
        setError('Failed to grant any permissions: ' + errors.join('; '));
      }
      
      setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to grant permissions');
      console.error('Error granting permissions:', err);
    }
  };

  const handleDeletePermission = async (permissionId, type) => {
    try {
      let response;
      switch (type) {
        case 'graph_source':
          response = await adminApi.revokeDataPermission(permissionId);
          break;
        case 'country':
          response = await adminApi.revokeCountryPermission(permissionId);
          break;
        case 'data':
          response = await adminApi.revokeDataPermission(permissionId);
          break;
        case 'financial':
          response = await adminApi.revokeFinancialDataPermission(permissionId);
          break;
        default:
          throw new Error('Invalid permission type');
      }
      
      // Reload permissions to reflect changes
      await loadPermissions();
      if (type === 'financial' && selectedFinancialUserId) {
        await loadFinancialPermissionsForUser(Number(selectedFinancialUserId));
      }
      notifyEmbeddedParent();
      setSuccess(response.message || 'Permission removed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove permission');
      console.error('Error removing permission:', err);
    }
  };

  const handleTogglePermission = async (permissionId, type, currentStatus, permissionObj?: any) => {
    try {
      const newStatus = !currentStatus;
      let response;

      // Call the appropriate API method based on permission type
      switch (type) {
        case 'graph_source':
          response = await adminApi.updateDataPermission(permissionId, newStatus);
          break;
        case 'country':
          response = await adminApi.updateCountryPermission(permissionId, newStatus);
          break;
        case 'data':
          // If attempting to deactivate own admin permission, require TOTP
          const isSelfAdminDeactivation = (
            permissionObj &&
            permissionObj.user_id === (currentUser?.id) &&
            permissionObj.data_type === 'admin' &&
            permissionObj.permission_level === 'admin' &&
            permissionObj.is_active === true &&
            newStatus === false
          );
          if (isSelfAdminDeactivation) {
            setPendingToggle({ id: permissionId, newStatus });
            setTotpCode('');
            setTotpOpen(true);
            return; // stop here; submit dialog will continue
          }
          response = await adminApi.updateDataPermission(permissionId, newStatus);
          break;
        default:
          console.error('Unknown permission type:', type);
          setError('Unknown permission type');
          return;
      }

      if (response) {
        // Update local state with the response from the API
        const updatePermission = (prev) => 
          prev.map(p => p.id === permissionId ? { ...p, is_active: newStatus } : p);
        
        switch (type) {
          case 'graph_source':
            setValueChainPermissions(updatePermission);
            break;
          case 'value_chain':
            setValueChainPermissions(updatePermission);
            break;
          case 'country':
            setCountryPermissions(updatePermission);
            break;
          case 'data':
            setDataPermissions(updatePermission);
            break;
        }
        
        notifyEmbeddedParent();
        setSuccess(`Permission ${newStatus ? 'activated' : 'deactivated'} successfully`);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update permission status');
      console.error('Error updating permission status:', err);
    }
  };

  const handleQuickGrantProdcomView = async () => {
    if (!dataFilterUserId) {
      setError('Select a user in Data Access first');
      return;
    }

    const targetUserId = Number(dataFilterUserId);
    const targetUser = users.find((u) => u.id === targetUserId);
    const userLabel = targetUser
      ? `${targetUser.first_name || ''} ${targetUser.last_name || ''}`.trim() || targetUser.email
      : `user ${targetUserId}`;

    try {
      await adminApi.grantDataPermission({
        user_id: targetUserId,
        data_type: 'prodcom_view',
        data_identifier: 'enabled',
        permission_level: 'read',
        metadata: null,
        notes: 'Granted via quick action',
      });
      await loadPermissions();
      notifyEmbeddedParent();
      setSuccess(`PRODCOM view granted to ${userLabel}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (String(detail || '').toLowerCase().includes('already')) {
        setError(`${userLabel} already has PRODCOM view permission`);
      } else {
        setError(detail || 'Failed to grant PRODCOM view permission');
      }
    }
  };

  const submitTotpDeactivation = async () => {
    if (!pendingToggle) { setTotpOpen(false); return; }
    try {
      // Single-item flow
      if (!pendingBulk) {
        const resp = await adminApi.updateDataPermission(pendingToggle.id, pendingToggle.newStatus, totpCode.trim());
        setTotpOpen(false);
        setPendingToggle(null);
        // Reflect state
        setDataPermissions((prev) => prev.map(p => p.id === resp.id ? { ...p, is_active: pendingToggle.newStatus } : p));
        notifyEmbeddedParent();
        setSuccess('Admin permission updated');
        setTimeout(() => setSuccess(null), 3000);
        return;
      }
      // Bulk flow
      const ids = pendingBulk.ids;
      const results = await Promise.allSettled(ids.map((id) => adminApi.updateDataPermission(id, pendingBulk.newStatus, totpCode.trim())));
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const fail = results.length - ok;
      setDataPermissions((prev) => prev.map(p => ids.includes(p.id) ? { ...p, is_active: pendingBulk.newStatus } : p));
      setTotpOpen(false);
      setPendingBulk(null);
      notifyEmbeddedParent();
      setSuccess(`${ok} updated, ${fail} failed`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'TOTP verification failed');
    }
  };

  const getCurrentPermissions = () => {
    switch (activeTab) {
      case 0: {
        let items = valueChainPermissions || [];
        if (!showInactiveGraphSources) items = items.filter((p: any) => p && p.is_active !== false);
        if (selectedGraphUserId) items = items.filter((p: any) => p && p.user_id === Number(selectedGraphUserId));
        return items;
      }
      case 1: {
        // Optionally filter out inactive and by user
        let items = countryPermissions || [];
        if (!showInactiveCountries) items = items.filter((p: any) => p && p.is_active !== false);
        if (selectedCountryUserId) items = items.filter((p: any) => p && p.user_id === Number(selectedCountryUserId));
        return items;
      }
      case 2: {
        // Exclude graph_source and country; apply filters and pagination
        let items = dataPermissions.filter((p: any) => p.data_type !== 'graph_source' && p.data_type !== 'country');
        if (dataFilterUserId) items = items.filter((p) => p.user_id === Number(dataFilterUserId));
        if (dataFilterType) items = items.filter((p) => p.data_type === dataFilterType);
        if (dataFilterStatus !== 'all') items = items.filter((p) => (dataFilterStatus === 'active') ? p.is_active : !p.is_active);
        // When showing 'All', optionally hide inactive unless toggled on
        if (dataFilterStatus === 'all' && !showInactiveData) {
          items = items.filter((p) => p && p.is_active !== false);
        }
        if (dataFilterSearch) {
          const q = dataFilterSearch.toLowerCase();
          items = items.filter((p) => `${p.user_email} ${p.user_name} ${p.data_type} ${p.data_identifier}`.toLowerCase().includes(q));
        }
        const start = (dataPage - 1) * dataPerPage;
        return items.slice(start, start + dataPerPage);
      }
      case 3: {
        const items = financialPermissions || [];
        return showInactiveFinancial ? items : items.filter((p: any) => p && p.is_active !== false);
      }
      default: return [];
    }
  };

  const getPermissionType = () => {
    switch (activeTab) {
      case 0: return 'graph_source'; // represented via UserDataPermissions(data_type='graph_source')
      case 1: return 'country';
      case 2: return 'data';
      case 3: return 'financial';
      default: return 'graph_source';
    }
  };

  const getPermissionTitle = () => {
    switch (activeTab) {
      case 0: return 'Value Chain Permissions';
      case 1: return 'Country Permissions';
      case 2: return 'Data Permissions';
      case 3: return 'Financial Data Permissions';
      default: return 'Permissions';
    }
  };

  const renderPermissionList = (permissions) => (
    <List>
      {permissions.map((permission) => (
        <React.Fragment key={permission.id}>
          <ListItem>
            <Checkbox
              edge="start"
              checked={(() => {
                const type = getPermissionType();
                if (type === 'graph_source') return selectedGraphSourceIds.includes(permission.id);
                if (type === 'country') return selectedCountryIds.includes(permission.id);
                if (type === 'data') return selectedDataIds.includes(permission.id);
                if (type === 'financial') return selectedFinancialIds.includes(permission.id);
                return false;
              })()}
              onChange={(e) => {
                const checked = e.target.checked;
                const type = getPermissionType();
                const id = permission.id;
                const updater = (arr: number[], set: any) => {
                  if (checked) set([...new Set([...arr, id])]);
                  else set(arr.filter((x) => x !== id));
                };
                if (type === 'graph_source') updater(selectedGraphSourceIds, setSelectedGraphSourceIds);
                else if (type === 'country') updater(selectedCountryIds, setSelectedCountryIds);
                else if (type === 'data') updater(selectedDataIds, setSelectedDataIds);
                else if (type === 'financial') updater(selectedFinancialIds, setSelectedFinancialIds);
              }}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle1">
                    {(() => {
                      if (activeTab === 3 && selectedFinancialUserId) {
                        const u = (users || []).find((x) => x.id === Number(selectedFinancialUserId));
                        if (u) {
                          const full = `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unknown User';
                          return `${full} (${u.email || 'No email'})`;
                        }
                      }
                      const name = permission.user_name || 'Unknown User';
                      const mail = permission.user_email || 'No email';
                      return `${name} (${mail})`;
                    })()}
                  </Typography>
                  <Chip
                    label={permission.permission_level}
                    size="small"
                    color={permission.permission_level === 'admin' ? 'error' : 'primary'}
                  />
                  <Chip
                    label={permission.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    color={permission.is_active ? 'success' : 'default'}
                  />
                </Box>
              }
              secondary={
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {activeTab === 0 && (() => {
                      const src = String(permission.data_identifier || '');
                      const found = (graphSources || []).find(s => String(s.source_db) === src);
                      return found ? found.label : (src || '—');
                    })()}
                    {activeTab === 1 && `${permission.country_name} (${permission.country_code})`}
                    {activeTab === 2 && `${permission.data_type} - ${permission.data_identifier}`}
                    {activeTab === 3 && `${permission.country} - ${permission.source}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Granted: {new Date(permission.granted_at).toLocaleDateString()} by {permission.granted_by}
                  </Typography>
                </Box>
              }
            />
            <ListItemSecondaryAction>
              <Box display="flex" gap={1}>
                <Tooltip title={permission.is_active ? 'Deactivate' : 'Activate'}>
                  <IconButton
                    size="small"
                    onClick={() => handleTogglePermission(permission.id, getPermissionType(), permission.is_active, permission)}
                    color={permission.is_active ? 'warning' : 'success'}
                  >
                    <Switch
                      checked={permission.is_active}
                      size="small"
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove Permission">
                  <IconButton
                    size="small"
                    onClick={() => handleDeletePermission(permission.id, getPermissionType())}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </ListItemSecondaryAction>
          </ListItem>
          <Divider />
        </React.Fragment>
      ))}
    </List>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const inner = (
      <Box>
        <Box
          display="flex"
          justifyContent={embedded ? 'flex-end' : 'space-between'}
          alignItems="center"
          mb={embedded ? 2 : 3}
        >
          {!embedded && (
            <Typography variant="h4" component="h1">
              Permission Management
            </Typography>
          )}
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadPermissions}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddPermission}
            >
              Add Permission
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            <Tab label="Graph Sources" />
            <Tab label="Countries" />
            <Tab label="Data Access" />
            <Tab label="Financial Data" />
          </Tabs>
        </Paper>

        {/* Permission List */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {getPermissionTitle()}
            </Typography>
            {/* Bulk actions toolbar */}
            <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(activeTab === 0) && (
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>User</InputLabel>
                  <Select
                    label="User"
                    value={selectedGraphUserId}
                    onChange={(e) => setSelectedGraphUserId(e.target.value as number | '')}
                    size="small"
                  >
                    <MenuItem value="">All users</MenuItem>
                    {(users || []).map((u) => (
                      <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {(activeTab === 0) && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showInactiveGraphSources}
                      onChange={(e) => setShowInactiveGraphSources(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show inactive"
                  sx={{ mr: 2 }}
                />
              )}
              {(activeTab === 1) && (
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>User</InputLabel>
                  <Select
                    label="User"
                    value={selectedCountryUserId}
                    onChange={(e) => setSelectedCountryUserId(e.target.value as number | '')}
                    size="small"
                  >
                    <MenuItem value="">All users</MenuItem>
                    {(users || []).map((u) => (
                      <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {(activeTab === 1) && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showInactiveCountries}
                      onChange={(e) => setShowInactiveCountries(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show inactive"
                  sx={{ mr: 2 }}
                />
              )}
              {(activeTab === 2) && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showInactiveData}
                      onChange={(e) => setShowInactiveData(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show inactive (when Status=All)"
                  sx={{ mr: 2 }}
                />
              )}
              {(activeTab === 3) && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showInactiveFinancial}
                      onChange={(e) => setShowInactiveFinancial(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Show inactive"
                  sx={{ mr: 2 }}
                />
              )}
              <Button
                size="small"
                onClick={() => {
                  const visible = getCurrentPermissions().map((p: any) => p.id);
                  const type = getPermissionType();
                  if (type === 'graph_source') setSelectedGraphSourceIds(visible);
                  if (type === 'country') setSelectedCountryIds(visible);
                  if (type === 'data') setSelectedDataIds(visible);
                  if (type === 'financial') setSelectedFinancialIds(visible);
                }}
              >
                Select All (visible)
              </Button>
              <Button
                size="small"
                onClick={() => {
                  const type = getPermissionType();
                  if (type === 'graph_source') setSelectedGraphSourceIds([]);
                  if (type === 'country') setSelectedCountryIds([]);
                  if (type === 'data') setSelectedDataIds([]);
                  if (type === 'financial') setSelectedFinancialIds([]);
                }}
              >
                Clear Selection
              </Button>
              {/* Activate/Deactivate for tabs that support update */}
              {getPermissionType() !== 'financial' && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={async () => {
                      const type = getPermissionType();
                      const ids = type === 'graph_source' ? selectedGraphSourceIds : type === 'country' ? selectedCountryIds : selectedDataIds;
                      if (!ids.length) return;
                      // For data tab, check 2FA requirement; open dialog and run bulk with TOTP
                      if (type === 'data') {
                        setPendingBulk({ ids, newStatus: true });
                        // No TOTP needed for activation
                        const results = await Promise.allSettled(ids.map((id) => adminApi.updateDataPermission(id, true)));
                        const ok = results.filter(r => r.status === 'fulfilled').length;
                        const fail = results.length - ok;
                        setDataPermissions((prev) => prev.map(p => ids.includes(p.id) ? { ...p, is_active: true } : p));
                        setSuccess(`${ok} activated, ${fail} failed`);
                        notifyEmbeddedParent();
                        setTimeout(() => setSuccess(null), 3000);
                        return;
                      }
                      if (type === 'graph_source') {
                        const results = await Promise.allSettled(ids.map((id) => adminApi.updateDataPermission(id, true)));
                        const ok = results.filter(r => r.status === 'fulfilled').length;
                        const fail = results.length - ok;
                        setValueChainPermissions((prev) => prev.map(p => ids.includes(p.id) ? { ...p, is_active: true } : p));
                        setSuccess(`${ok} activated, ${fail} failed`);
                        notifyEmbeddedParent();
                        setTimeout(() => setSuccess(null), 3000);
                      } else if (type === 'country') {
                        const results = await Promise.allSettled(ids.map((id) => adminApi.updateCountryPermission(id, true)));
                        const ok = results.filter(r => r.status === 'fulfilled').length;
                        const fail = results.length - ok;
                        setCountryPermissions((prev) => prev.map(p => ids.includes(p.id) ? { ...p, is_active: true } : p));
                        setSuccess(`${ok} activated, ${fail} failed`);
                        notifyEmbeddedParent();
                        setTimeout(() => setSuccess(null), 3000);
                      }
                    }}
                  >
                    Activate Selected
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={async () => {
                      const type = getPermissionType();
                      const ids = type === 'graph_source' ? selectedGraphSourceIds : type === 'country' ? selectedCountryIds : selectedDataIds;
                      if (!ids.length) return;
                      if (type === 'data') {
                        // May require TOTP; prompt once and reuse
                        setPendingBulk({ ids, newStatus: false });
                        setTotpCode('');
                        setTotpOpen(true);
                        return;
                      }
                      if (type === 'graph_source') {
                        const results = await Promise.allSettled(ids.map((id) => adminApi.updateDataPermission(id, false)));
                        const ok = results.filter(r => r.status === 'fulfilled').length;
                        const fail = results.length - ok;
                        setValueChainPermissions((prev) => prev.map(p => ids.includes(p.id) ? { ...p, is_active: false } : p));
                        setSuccess(`${ok} deactivated, ${fail} failed`);
                        notifyEmbeddedParent();
                        setTimeout(() => setSuccess(null), 3000);
                      } else if (type === 'country') {
                        const results = await Promise.allSettled(ids.map((id) => adminApi.updateCountryPermission(id, false)));
                        const ok = results.filter(r => r.status === 'fulfilled').length;
                        const fail = results.length - ok;
                        setCountryPermissions((prev) => prev.map(p => ids.includes(p.id) ? { ...p, is_active: false } : p));
                        setSuccess(`${ok} deactivated, ${fail} failed`);
                        notifyEmbeddedParent();
                        setTimeout(() => setSuccess(null), 3000);
                      }
                    }}
                  >
                    Deactivate Selected
                  </Button>
                </>
              )}
              {/* Revoke for all tabs */}
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={async () => {
                  const type = getPermissionType();
                  let ids: number[] = [];
                  if (type === 'graph_source') ids = selectedGraphSourceIds;
                  if (type === 'country') ids = selectedCountryIds;
                  if (type === 'data') ids = selectedDataIds;
                  if (type === 'financial') ids = selectedFinancialIds;
                  if (!ids.length) return;
                  if (type === 'graph_source' || type === 'data') {
                    const results = await Promise.allSettled(ids.map((id) => adminApi.revokeDataPermission(id)));
                    const ok = results.filter(r => r.status === 'fulfilled').length;
                    const fail = results.length - ok;
                    if (type === 'graph_source') setValueChainPermissions((prev) => prev.filter(p => !ids.includes(p.id)));
                    else setDataPermissions((prev) => prev.filter(p => !ids.includes(p.id)));
                    setSuccess(`${ok} revoked, ${fail} failed`);
                  } else if (type === 'country') {
                    const results = await Promise.allSettled(ids.map((id) => adminApi.revokeCountryPermission(id)));
                    const ok = results.filter(r => r.status === 'fulfilled').length;
                    const fail = results.length - ok;
                    setCountryPermissions((prev) => prev.filter(p => !ids.includes(p.id)));
                    setSuccess(`${ok} revoked, ${fail} failed`);
                  } else if (type === 'financial') {
                    const results = await Promise.allSettled(ids.map((id) => adminApi.revokeFinancialDataPermission(id)));
                    const ok = results.filter(r => r.status === 'fulfilled').length;
                    const fail = results.length - ok;
                    setFinancialPermissions((prev) => prev.filter(p => !ids.includes(p.id)));
                    setSuccess(`${ok} revoked, ${fail} failed`);
                  }
                  notifyEmbeddedParent();
                  setTimeout(() => setSuccess(null), 3000);
                }}
              >
                Revoke Selected
              </Button>
            </Box>
            {activeTab === 3 && (
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 260 }}>
                  <InputLabel>User</InputLabel>
                  <Select
                    label="User"
                    value={selectedFinancialUserId}
                    onChange={async (e) => {
                      const v = e.target.value as number | '';
                      setSelectedFinancialUserId(v);
                      setFinancialPage(1);
                      if (v === '' || v === ('' as any)) {
                        await loadAllFinancialPermissions();
                      } else {
                        await loadFinancialPermissionsForUser(Number(v));
                      }
                    }}
                  >
                    <MenuItem value="">All users</MenuItem>
                    {(users || []).map((u) => (
                      <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" onClick={() => {
                  if (selectedFinancialUserId) return loadFinancialPermissionsForUser(Number(selectedFinancialUserId));
                  return loadAllFinancialPermissions();
                }}>Refresh</Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setPermissionType('financial');
                    setFormData((prev) => ({ ...prev, userIds: selectedFinancialUserId ? [Number(selectedFinancialUserId)] : [] }));
                    setAddPermissionDialog(true);
                  }}
                  disabled={!selectedFinancialUserId}
                >
                  Add Financial Permission
                </Button>
              </Box>
            )}
            {activeTab === 2 && (
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>User</InputLabel>
                  <Select label="User" value={dataFilterUserId} onChange={(e) => { setDataFilterUserId(e.target.value as number | ''); setDataPage(1); }}>
                    <MenuItem value="">All users</MenuItem>
                    {(users || []).map((u) => (
                      <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel>Type</InputLabel>
                  <Select label="Type" value={dataFilterType} onChange={(e) => { setDataFilterType(e.target.value); setDataPage(1); }}>
                    <MenuItem value="">All</MenuItem>
                    {(dataTypeOptions || []).filter((t) => t !== 'graph_source' && t !== 'country').map((t) => (
                      <MenuItem key={t} value={t}>{t === 'admin' ? 'Admin Access' : t === 'prodcom_view' ? 'PRODCOM View' : t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select label="Status" value={dataFilterStatus} onChange={(e) => { setDataFilterStatus(e.target.value as any); setDataPage(1); }}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="Search" value={dataFilterSearch} onChange={(e) => { setDataFilterSearch(e.target.value); setDataPage(1); }} sx={{ minWidth: 240 }} />
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleQuickGrantProdcomView}
                  disabled={!dataFilterUserId}
                >
                  Grant PRODCOM View
                </Button>
              </Box>
            )}
            {activeTab === 3 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                <Button
                  disabled={financialPage === 1}
                  onClick={async () => {
                    const newPage = Math.max(1, financialPage - 1);
                    setFinancialPage(newPage);
                    if (selectedFinancialUserId) await adminApi.getFinancialPermissions({ user_id: Number(selectedFinancialUserId), page: newPage, limit: financialPerPage }).then(resp => {
                      setFinancialPermissions(resp.permissions || []); setFinancialTotal(resp.total || 0);
                    }).catch(() => {});
                    else await adminApi.getFinancialPermissions({ page: newPage, limit: financialPerPage }).then(resp => {
                      setFinancialPermissions(resp.permissions || []); setFinancialTotal(resp.total || 0);
                    }).catch(() => {});
                  }}
                >
                  Prev
                </Button>
                <Typography variant="body2" sx={{ alignSelf: 'center' }}>Page {financialPage}</Typography>
                <Button
                  disabled={(financialPage * financialPerPage) >= financialTotal}
                  onClick={async () => {
                    const newPage = financialPage + 1;
                    setFinancialPage(newPage);
                    if (selectedFinancialUserId) await adminApi.getFinancialPermissions({ user_id: Number(selectedFinancialUserId), page: newPage, limit: financialPerPage }).then(resp => {
                      setFinancialPermissions(resp.permissions || []); setFinancialTotal(resp.total || 0);
                    }).catch(() => {});
                    else await adminApi.getFinancialPermissions({ page: newPage, limit: financialPerPage }).then(resp => {
                      setFinancialPermissions(resp.permissions || []); setFinancialTotal(resp.total || 0);
                    }).catch(() => {});
                  }}
                >
                  Next
                </Button>
              </Box>
            )}
            {getCurrentPermissions().length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No permissions found. Click "Add Permission" to grant access.
              </Typography>
            ) : (
              renderPermissionList(getCurrentPermissions())
            )}
            {activeTab === 2 && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                <Button disabled={dataPage === 1} onClick={() => setDataPage((p) => Math.max(1, p - 1))}>Prev</Button>
                <Typography variant="body2" sx={{ alignSelf: 'center' }}>Page {dataPage}</Typography>
                <Button onClick={() => setDataPage((p) => p + 1)}>Next</Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Add Permission Dialog */}
        <Dialog open={addPermissionDialog} onClose={() => setAddPermissionDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New Permission</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <FormControl fullWidth>
                <InputLabel>Permission Type</InputLabel>
                <Select
                  value={permissionType}
                  onChange={(e) => setPermissionType(e.target.value)}
                  label="Permission Type"
                >
                  <MenuItem value="value_chain">Graph Source Access</MenuItem>
                  <MenuItem value="country">Country Access</MenuItem>
                  <MenuItem value="data">Data Permission</MenuItem>
                  <MenuItem value="financial">Financial Data Permission</MenuItem>
                </Select>
              </FormControl>
              
                  <FormControl fullWidth>
                    <InputLabel>Users</InputLabel>
                    <Select
                      multiple
                      value={formData.userIds}
                      onChange={(e) => setFormData({...formData, userIds: Array.isArray(e.target.value) ? e.target.value : [e.target.value]})}
                      label="Users"
                      disabled={loadingDropdowns}
                      renderValue={(selected) => {
                        if (selected.length === 0) return '';
                        if (selected.length === 1) {
                          const user = users.find(u => u.id === selected[0]);
                          return user ? `${user.first_name} ${user.last_name}` : '';
                        }
                        return `${selected.length} users selected`;
                      }}
                    >
                      {(users || []).map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {formData.userIds.length === 0 ? 'Select one or more users' : 
                       `${formData.userIds.length} user${formData.userIds.length === 1 ? '' : 's'} selected`}
                    </Typography>
                  </FormControl>
              
              {permissionType === 'value_chain' && (
                <FormControl fullWidth>
                  <InputLabel>Graph Sources</InputLabel>
                  <Select
                    multiple
                    value={formData.valueChainIds}
                    onChange={(e) => {
                      const selectedIds = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                      const selectedNames = selectedIds.map(id => String(id));
                      setFormData({
                        ...formData, 
                        valueChainIds: selectedIds,
                        valueChainNames: selectedNames
                      });
                    }}
                    label="Graph Sources"
                    disabled={loadingDropdowns}
                    renderValue={(selected) => {
                      if (selected.length === 0) return '';
                      if (selected.length === 1) return selected[0];
                      return `${selected.length} sources selected`;
                    }}
                  >
                    {(graphSources || []).map((s) => (
                      <MenuItem key={s.source_db} value={s.source_db}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {formData.valueChainIds.length === 0 ? 'Select one or more sources' : 
                     `${formData.valueChainIds.length} source${formData.valueChainIds.length === 1 ? '' : 's'} selected`}
                  </Typography>
                </FormControl>
              )}
              
              {permissionType === 'country' && (
                <Box>
                  <Autocomplete
                    multiple
                    options={countries || []}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    value={(countries || []).filter(c => formData.countryCodes.includes(c.code))}
                    onChange={(event, newValue) => {
                      const selectedCodes = newValue.map(c => c.code);
                      const selectedNames = newValue.map(c => c.name);
                      setFormData({
                        ...formData,
                        countryCodes: selectedCodes,
                        countryNames: selectedNames
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Countries"
                        placeholder="Search countries..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.code}
                          label={option.code}
                          size="small"
                        />
                      ))
                    }
                    disabled={loadingDropdowns}
                    filterSelectedOptions
                    fullWidth
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {formData.countryCodes.length === 0 ? 'Select one or more countries' : 
                     `${formData.countryCodes.length} countr${formData.countryCodes.length === 1 ? 'y' : 'ies'} selected`}
                  </Typography>
                </Box>
              )}
              
              {permissionType === 'data' && (
                <>
                  <FormControl fullWidth>
                    <InputLabel>Data Type</InputLabel>
                    <Select
                      value={formData.dataType}
                      onChange={(e) => setFormData({...formData, dataType: e.target.value})}
                      label="Data Type"
                    >
                      {(dataTypeOptions.length ? dataTypeOptions : ['admin', 'prodcom_view']).map((t) => (
                        <MenuItem key={t} value={t}>
                          {t === 'admin' ? 'Admin Access' : t === 'prodcom_view' ? 'PRODCOM View' : t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label="Data Identifier"
                    fullWidth
                    value={formData.dataIdentifier}
                    onChange={(e) => setFormData({...formData, dataIdentifier: e.target.value})}
                    placeholder="e.g., * (for all) or specific identifier"
                  />
                </>
              )}

              {permissionType === 'financial' && (
                <Box>
                  <Autocomplete
                    multiple
                    options={financialSources || []}
                    getOptionLabel={(option) => option.label}
                    onChange={(event, newValue) => {
                      const combos = newValue.map((v) => ({ country: v.country, source: v.source }));
                      setFormData({ ...formData, financialCombos: combos });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Financial Data (Country • Source)"
                        placeholder="Search country/source..."
                      />
                    )}
                    fullWidth
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {(!formData.financialCombos || formData.financialCombos.length === 0) ? 'Select one or more (country, source) combinations' : `${formData.financialCombos.length} combination(s) selected`}
                  </Typography>
                </Box>
              )}
              
              <FormControl fullWidth>
                <InputLabel>Permission Level</InputLabel>
                <Select
                  value={formData.permissionLevel}
                  onChange={(e) => setFormData({...formData, permissionLevel: e.target.value})}
                  label="Permission Level"
                >
                  <MenuItem value="read">Read</MenuItem>
                  <MenuItem value="write">Write</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
              
              <FormControlLabel
                control={
                  <Switch 
                    checked={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  />
                }
                label="Active"
              />
              
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Optional notes about this permission"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddPermissionDialog(false)}>Cancel</Button>
                <Button 
                  variant="contained" 
                  startIcon={<SaveIcon />}
                  onClick={handleFormSubmit}
                  disabled={loadingDropdowns || formData.userIds.length === 0 || 
                    (permissionType === 'value_chain' && formData.valueChainIds.length === 0) ||
                    (permissionType === 'country' && formData.countryCodes.length === 0) ||
                    (permissionType === 'data' && !formData.dataType) ||
                    (permissionType === 'financial' && (!formData.financialCombos || formData.financialCombos.length === 0))
                  }
                >
                  Grant Permission{formData.userIds.length > 1 || 
                    (permissionType === 'value_chain' && formData.valueChainIds.length > 1) ||
                    (permissionType === 'country' && formData.countryCodes.length > 1) ||
                    (permissionType === 'financial' && formData.financialCombos && formData.financialCombos.length > 1) ? 's' : ''}
                </Button>
          </DialogActions>
        </Dialog>

        {/* TOTP dialog for self-admin deactivation */}
        <Dialog open={totpOpen} onClose={() => setTotpOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Enter 2FA code</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="6-digit TOTP"
              fullWidth
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTotpOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={submitTotpDeactivation} disabled={totpCode.length !== 6}>Verify</Button>
          </DialogActions>
        </Dialog>
      </Box>
  );

  if (embedded) {
    return inner;
  }

  return (
    <PermissionGate 
      requiredPermissions={['admin']}
      fallback={
        <Alert severity="error">
          Admin permissions required to access permission management.
        </Alert>
      }
    >
      {inner}
    </PermissionGate>
  );
};

export default PermissionManagement;
